import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { PopDisplays_Display } from 'c-sdk';
import { Alert, Button, DialogV2, MuiRouterLink } from 'c-components';
import { useCommonTranslation } from 'c-translation';
import { generatePath } from 'react-router-dom';
import { PostAuthRoutes } from 'c-routes';
import type { DataGridProProps } from '@mui/x-data-grid-pro';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useAPIClientRequest } from 'c-hooks';
import ApiClient from 'c-data/apiClient';
import { CircularProgress, IconButton, Stack } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import to from 'await-to-js';

type Props = {
    displays: PopDisplays_Display[];
    id: string | number;
};

const DisplayDetailsTable: React.FC<Props> = ({ displays, id }) => {
    const t = useCommonTranslation();
    const [deleteId, setDeleteId] = useState<string | number | null>(null);
    const [rows, setRows] = useState<PopDisplays_Display[]>(displays);
    useEffect(() => {
        setRows(displays);
    }, [displays]);
    const { start, error, isLoading, reset } = useAPIClientRequest(
        ApiClient.PopDisplays.removeDisplayPortalId,
    );
    const handleRemoveDisplayPortalId = useCallback(async () => {
        if (deleteId !== null) {
            const payload =
                typeof deleteId === 'string' ? ([deleteId] as string[]) : ([deleteId] as number[]);
            const [err, succ] = await to(start(id, payload));
            if (!err && succ) {
                setRows(rows.filter(row => row.id !== deleteId));
                setDeleteId(null);
            }
        }
    }, [deleteId, id, rows, start]);
    const columns = useMemo<DataGridProProps['columns']>(
        () => [
            {
                field: 'id',
                headerName: t('Modules.PopDisplays.Display.table.idCol'),
                sortable: true,
                flex: 1,
                renderCell: params => (
                    <Stack direction="row" gap={1} alignItems="center">
                        <IconButton onClick={() => setDeleteId(params.id)}>
                            <DeleteIcon color="error" />
                        </IconButton>
                        <MuiRouterLink
                            to={generatePath(PostAuthRoutes.Displays.DisplaysDisplayEdit, {
                                id: params.id,
                            })}
                        >
                            {params.id}
                        </MuiRouterLink>
                    </Stack>
                ),
            },
            {
                field: 'address',
                headerName: t('Modules.PopDisplays.Display.table.addressCol'),
                sortable: true,
                flex: 3,
            },
            {
                field: 'frame_id',
                headerName: t('Modules.PopDisplays.Display.table.frameIdCol'),
                sortable: true,
                minWidth: 120,
            },
        ],
        [t],
    );

    const handleCloseDialog = useCallback(() => {
        setDeleteId(null);
        reset();
    }, [reset]);

    return (
        <>
            <DialogV2
                onClose={handleCloseDialog}
                open={deleteId !== null}
                title={t('Modules.PopDisplays.Display.deleteDisplay', { id: deleteId })}
            >
                <Stack gap={1} alignItems="center">
                    {!isLoading && (
                        <Stack direction="row" gap={1} alignItems="center" justifyContent="center">
                            <Button onClick={handleRemoveDisplayPortalId}>
                                {t('Modules.PopDisplays.Display.yes')}
                            </Button>
                            <Button onClick={handleCloseDialog}>
                                {t('Modules.PopDisplays.Display.no')}
                            </Button>
                        </Stack>
                    )}
                    {isLoading && <CircularProgress />}
                    {error && (
                        <Alert severity="error" onClose={reset}>
                            {String(error)}
                        </Alert>
                    )}
                </Stack>
            </DialogV2>
            <DataGridPro
                autoHeight
                disableRowSelectionOnClick
                disableColumnReorder
                disableColumnSelector
                disableColumnPinning
                hideFooter
                columns={columns}
                rows={rows}
            />
        </>
    );
};

export default DisplayDetailsTable;
